import { isEqual, omit } from "lodash";

import { crmScopeOptionMap } from "app/pages/sales-intelligence/domain/leads/types/filter-values";

import type { ILeadsSearchFiltersDto } from "../../types/leadsSearchFilters.dto";

// Signals are owned by the signals slice (see selectComposedFiltersValue); the leads-filters
// slice must never persist them, so they are dropped before the resolved value is stored.
const withoutSignals = (filters: Partial<ILeadsSearchFiltersDto>): Partial<ILeadsSearchFiltersDto> =>
    "signalsFilters" in filters ? omit(filters, "signalsFilters") : filters;

const DEEP_MERGE_CATEGORIES: Partial<keyof ILeadsSearchFiltersDto>[] = [
    "contactsFilters",
    "leadsFilters",
    "appsFilters",
];

const mergeCategory = (
    key: (typeof DEEP_MERGE_CATEGORIES)[number],
    currentFilters: Partial<ILeadsSearchFiltersDto>,
    incomingFilters: Partial<ILeadsSearchFiltersDto>,
): Partial<ILeadsSearchFiltersDto> =>
    currentFilters[key] || incomingFilters[key]
        ? ({ [key]: { ...currentFilters[key], ...incomingFilters[key] } } as Partial<ILeadsSearchFiltersDto>)
        : {};

/**
 * "Net new" means "not in the CRM", so it cannot coexist with a CRM condition (which only ever matches records
 * that ARE in the CRM). The side the user just changed wins: applying a condition moves the scope to `inCrm`,
 * while picking "net new" drops the conditions.
 */
const resolveCrmScope = (
    currentFilters: Partial<ILeadsSearchFiltersDto>,
    incomingFilters: Partial<ILeadsSearchFiltersDto>,
): Partial<ILeadsSearchFiltersDto> => {
    if (incomingFilters.leadsFilters?.crmScope !== crmScopeOptionMap.netNew) {
        return incomingFilters;
    }

    const isScopeChanged = currentFilters.leadsFilters?.crmScope !== crmScopeOptionMap.netNew;
    const areConditionsChanged = !isEqual(
        currentFilters.salesforceCustomFilters ?? [],
        incomingFilters.salesforceCustomFilters ?? [],
    );

    if (isScopeChanged && !areConditionsChanged) {
        return omit(incomingFilters, "salesforceCustomFilters");
    }

    return {
        ...incomingFilters,
        leadsFilters: { ...incomingFilters.leadsFilters, crmScope: crmScopeOptionMap.inCrm },
    };
};

// An empty CRM filter set is equivalent to no CRM filters at all, so the key is dropped. Only clone when
// something actually changes — callers rely on the identity of an unchanged result.
const normalizeCrmFilters = (
    currentFilters: Partial<ILeadsSearchFiltersDto>,
    incomingFilters: Partial<ILeadsSearchFiltersDto>,
): Partial<ILeadsSearchFiltersDto> => {
    if (incomingFilters.salesforceCustomFilters?.length) {
        return resolveCrmScope(currentFilters, incomingFilters);
    }

    return "salesforceCustomFilters" in incomingFilters
        ? omit(incomingFilters, "salesforceCustomFilters")
        : incomingFilters;
};

export const resolveStateReset = (
    currentFilters: Partial<ILeadsSearchFiltersDto>,
    incomingFilters: Partial<ILeadsSearchFiltersDto>,
): Partial<ILeadsSearchFiltersDto> => {
    let resolved = withoutSignals(incomingFilters);

    const currentMarket = currentFilters.appsFilters?.market;
    const incomingMarket = resolved.appsFilters?.market;
    if (incomingMarket && currentMarket && incomingMarket !== currentMarket) {
        resolved = { ...resolved, appsFilters: { market: incomingMarket } };
    }

    return normalizeCrmFilters(currentFilters, resolved);
};

// Merges partial incoming filters into current state, then resolves apps market reset
export const resolveFiltersState = (
    currentFilters: Partial<ILeadsSearchFiltersDto>,
    incomingFilters: Partial<ILeadsSearchFiltersDto>,
): Partial<ILeadsSearchFiltersDto> => {
    const mergedFilters = DEEP_MERGE_CATEGORIES.reduce(
        (acc, key) => ({ ...acc, ...mergeCategory(key, currentFilters, incomingFilters) }),
        { ...currentFilters, ...incomingFilters },
    );

    return resolveStateReset(currentFilters, mergedFilters);
};
